import { useState, useEffect, useRef } from 'react'
import { X, ChevronLeft, ShoppingBag, Tag } from 'lucide-react'
import { notifySheetOpened, notifySheetClosed } from '../widgets.jsx'
import { useToast } from '../components/boothly/toast-provider.jsx'

export function CreateBereichSheet({ open, onOpenChange, userStores, onCreateStore, onJoinStore, dark, initialType, initialStep }) {
  const toast = useToast()
  const [step, setStep] = useState('channel')
  const [selectedType, setSelectedType] = useState(null)
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const inputRef = useRef(null)

  // Join-Flow (Bereich per Einladungscode beitreten)
  const [code, setCode] = useState('')
  const [joining, setJoining] = useState(false)
  const [joinError, setJoinError] = useState(null)
  const codeInputRef = useRef(null)

  const hasFlea = !!(userStores || []).find(s => s.type === 'flea')

  // Reset state when sheet opens/closes
  useEffect(() => {
    if (open) {
      if (initialStep === 'join') {
        setStep('join')
        setSelectedType(null)
        setName('')
      } else if (initialType) {
        setSelectedType(initialType)
        setName(initialType === 'flea' ? 'Trödel' : '')
        setStep('name')
      } else {
        setStep('channel')
        setSelectedType(null)
        setName('')
      }
      setSubmitting(false)
      setCode('')
      setJoinError(null)
      setJoining(false)
    }
  }, [open, initialType, initialStep])

  // Sheet open/close notifications (hides BottomNav)
  useEffect(() => {
    if (!open) return
    notifySheetOpened()
    return () => notifySheetClosed()
  }, [open])

  // Autofocus name/code input when entering step 2
  useEffect(() => {
    if (step === 'name') {
      setTimeout(() => inputRef.current?.focus(), 350)
    } else if (step === 'join') {
      setTimeout(() => codeInputRef.current?.focus(), 350)
    }
  }, [step])

  if (!open) return null

  const selectChannel = (type) => {
    setSelectedType(type)
    setName(type === 'flea' ? 'Trödel' : '')
    setStep('name')
  }

  const handleSubmit = async () => {
    if (!name.trim() || !selectedType || submitting) return
    setSubmitting(true)
    try {
      await onCreateStore(name.trim(), selectedType)
      onOpenChange(false)
      toast.success('Bereich erstellt')
    } catch (e) {
      toast.error(e.message || 'Anlegen fehlgeschlagen')
    } finally {
      setSubmitting(false)
    }
  }

  const handleJoin = async () => {
    const cleaned = code.trim().toUpperCase().replace(/\s+/g, '')
    if (!cleaned) { setJoinError('Code eingeben'); return }
    if (joining) return
    setJoining(true)
    setJoinError(null)
    try {
      const data = await onJoinStore(cleaned)
      onOpenChange(false)
      toast.success(
        data?.already_member
          ? `Bereits Mitglied von ${data?.store?.name || 'Bereich'}`
          : `${data?.store?.name || 'Bereich'} beigetreten`
      )
    } catch (e) {
      setJoinError(e.message || 'Code ungültig oder abgelaufen')
    } finally {
      setJoining(false)
    }
  }

  const accent = selectedType === 'flea' ? 'var(--accent-troedel)' : 'var(--accent-online)'

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[2000] bg-black/40 backdrop-blur-[20px]"
        style={{ animation: 'fadeIn 0.2s ease' }}
        onClick={() => onOpenChange(false)}
      />

      {/* Sheet */}
      <div
        className="fixed inset-x-0 bottom-0 z-[2001] flex max-h-[92vh] flex-col rounded-t-3xl"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          animation: 'slideUpSheet 0.3s cubic-bezier(0.32,0.72,0,1) backwards',
          background: dark ? '#2C2C2E' : '#FFFFFF',
        }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="bottom-sheet-handle" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-4">
          {step !== 'channel' ? (
            <button
              onClick={() => setStep('channel')}
              className="flex items-center gap-1 text-[var(--label-secondary)] active:opacity-70 transition-opacity"
            >
              <ChevronLeft size={20} />
              <span className="text-[15px]">Zurück</span>
            </button>
          ) : (
            <h2 className="text-xl font-semibold">Neuer Bereich</h2>
          )}
          <button onClick={() => onOpenChange(false)} className="w-8 h-8 flex items-center justify-center">
            <X className="h-5 w-5 text-[var(--label-secondary)]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-6">
          {step === 'channel' && (
            <div className="space-y-3">
              {/* Online card */}
              <div
                onClick={() => selectChannel('online')}
                className="rounded-2xl p-4 flex items-center gap-4 cursor-pointer active:scale-[0.98] transition-transform border"
                style={{
                  background: 'var(--tint-online)',
                  borderColor: 'var(--border-default)',
                }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-online)' }}>
                  <ShoppingBag size={22} className="text-white" />
                </div>
                <div>
                  <div className="text-[17px] font-semibold">Online-Store</div>
                  <div className="text-[13px] text-[var(--label-secondary)] mt-0.5">Für eBay, Etsy, Shopify & Co.</div>
                </div>
              </div>

              {/* Trödel card */}
              <div
                onClick={hasFlea ? undefined : () => selectChannel('flea')}
                className={`rounded-2xl p-4 flex items-center gap-4 border ${hasFlea ? 'opacity-50 cursor-default' : 'cursor-pointer active:scale-[0.98]'} transition-transform`}
                style={{
                  background: 'var(--tint-troedel)',
                  borderColor: 'var(--border-default)',
                }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-troedel)' }}>
                  <Tag size={22} className="text-white" />
                </div>
                <div>
                  <div className="text-[17px] font-semibold">Trödel</div>
                  <div className="text-[13px] text-[var(--label-secondary)] mt-0.5">Flohmarkt-Verkäufe verwalten</div>
                </div>
              </div>

              {hasFlea && (
                <p className="text-[12px] text-[var(--label-tertiary)] px-1">
                  Du hast bereits einen Trödel-Bereich.
                </p>
              )}

              {/* Dezenter Sekundär-Weg: einem fremden Bereich als Helfer beitreten */}
              <button
                onClick={() => setStep('join')}
                className="w-full text-center text-[13px] text-[var(--label-secondary)] pt-2 pb-1 active:opacity-70 transition-opacity"
              >
                Einladungscode? Bereich beitreten
              </button>
            </div>
          )}

          {step === 'join' && (
            <div className="space-y-5">
              <div>
                <label className="text-[13px] text-[var(--label-secondary)] mb-2 block">Einladungscode</label>
                <input
                  ref={codeInputRef}
                  type="text"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.toUpperCase()); setJoinError(null) }}
                  placeholder="z.B. K7Q9X3F2"
                  maxLength={12}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  className="w-full rounded-xl px-4 py-3 text-[15px] outline-none tracking-[0.15em]"
                  style={{
                    background: dark ? 'var(--fill-tertiary)' : 'var(--fill-quaternary)',
                    border: joinError ? '1px solid var(--accent-red)' : '1px solid transparent',
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleJoin() }}
                />
                {joinError && (
                  <p className="text-[12px] mt-2" style={{ color: 'var(--accent-red)' }}>{joinError}</p>
                )}
                <p className="text-[12px] text-[var(--label-tertiary)] mt-2 px-0.5">
                  Du trittst dem Bereich als Helfer bei und kannst beim Versand unterstützen.
                </p>
              </div>

              {/* Neutral — Beitreten ist kein Kanal, kein Channel-Akzent */}
              <button
                onClick={handleJoin}
                disabled={!code.trim() || joining}
                className="w-full rounded-xl py-4 font-semibold transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
                style={{ background: 'var(--fill-tertiary)', color: 'var(--label-primary)' }}
              >
                {joining ? 'Wird beigetreten…' : 'Beitreten'}
              </button>
            </div>
          )}

          {step === 'name' && (
            <div className="space-y-5">
              {/* Channel pill */}
              <span
                className="inline-block text-[11px] font-medium px-3 py-1 rounded-full"
                style={{
                  background: selectedType === 'flea' ? 'var(--tint-troedel)' : 'var(--tint-online)',
                  color: accent,
                }}
              >
                {selectedType === 'flea' ? 'Trödel' : 'Online-Store'}
              </span>

              {/* Name input */}
              <div>
                <label className="text-[13px] text-[var(--label-secondary)] mb-2 block">Name</label>
                <input
                  ref={inputRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={selectedType === 'flea' ? 'z.B. Trödel' : 'z.B. Mein Shop'}
                  maxLength={200}
                  className="w-full rounded-xl px-4 py-3 text-[15px] outline-none"
                  style={{
                    background: dark ? 'var(--fill-tertiary)' : 'var(--fill-quaternary)',
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit() }}
                />
              </div>

              {/* Submit button */}
              <button
                onClick={handleSubmit}
                disabled={!name.trim() || submitting}
                className="w-full rounded-xl py-4 font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
                style={{ background: accent }}
              >
                {submitting ? 'Wird erstellt…' : 'Bereich erstellen'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
